import { bandForGrade, type PracticeBand } from "./practiceBands";
import { gradeFullLabel, gradeSpanLabel } from "./practiceGrades";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeSubject } from "./practiceSubjects";

/**
 * Grade → topic → skill map for SEO hubs and the practice catalog.
 * A topic keeps one URL. Grades that share it link to that same page.
 * `breadcrumbGrade` is the hub used in Home › Grade N › Topic.
 */
export type GradeTopicLink = {
  slug: string;
  gradeMin: number;
  gradeMax: number;
  breadcrumbGrade: number;
  skillId: string;
  level: PracticeBand;
  subject: PracticeSubject;
  navLabel: { en: string; he: string };
  /** One sentence on each grade hub that includes this topic. */
  summary: { en: string; he: string };
};

export type GradeSample = {
  skillId: string;
  level: PracticeBand;
  subject: PracticeSubject;
  name: { en: string; he: string };
  sentence: { en: string; he: string };
};

export type GradeHubCopy = {
  title: string;
  description: string;
  keywords: string;
  heading: string;
  paragraphs: string[];
  cta: string;
};

export type GradeHub = {
  grade: number;
  samples: readonly GradeSample[];
  en: GradeHubCopy;
  he: GradeHubCopy;
};

export const GRADE_TOPIC_LINKS: readonly GradeTopicLink[] = [
  {
    slug: "fractions",
    gradeMin: 3,
    gradeMax: 5,
    breadcrumbGrade: 4,
    skillId: "arithmetic.add-fractions-same-den",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Fractions", he: "שברים" },
    summary: {
      en: "Name equal parts, build equivalent fractions, and add fractions that share a denominator.",
      he: "נותנים שם לחלקים שווים, בונים שברים שקולים, ומחברים שברים עם אותו מכנה.",
    },
  },
  {
    slug: "long-division",
    gradeMin: 4,
    gradeMax: 4,
    breadcrumbGrade: 4,
    skillId: "arithmetic.divide-remainder",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Long division", he: "חילוק ארוך" },
    summary: {
      en: "Divide a multi-digit number by a one-digit divisor and keep the remainder smaller than the divisor.",
      he: "מחלקים מספר רב-ספרתי במחלק חד-ספרתי, והשארית נשארת קטנה מהמחלק.",
    },
  },
  {
    slug: "order-of-operations",
    gradeMin: 5,
    gradeMax: 6,
    breadcrumbGrade: 5,
    skillId: "arithmetic.pemdas",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Order of operations", he: "סדר פעולות" },
    summary: {
      en: "Do the work inside parentheses first, then multiply and divide before you add and subtract.",
      he: "קודם מחשבים את מה שבתוך הסוגריים, ואחר כך כפל וחילוק לפני חיבור וחיסור.",
    },
  },
  {
    slug: "linear-equations",
    gradeMin: 7,
    gradeMax: 8,
    breadcrumbGrade: 7,
    skillId: "algebra.linear-one-step",
    level: "middle",
    subject: "Algebra",
    navLabel: { en: "Linear equations", he: "משוואות לינאריות" },
    summary: {
      en: "Keep both sides of an equation equal while you undo one operation and solve for the unknown.",
      he: "שומרים על שוויון בשני האגפים, מבטלים פעולה אחת, ופותרים לנעלם.",
    },
  },
  {
    slug: "place-value",
    gradeMin: 3,
    gradeMax: 3,
    breadcrumbGrade: 3,
    skillId: "arithmetic.place-hundreds",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Place value", he: "ערך מקום" },
    summary: {
      en: "Read the hundreds, tens, and ones in a three-digit number, including a zero in the tens place.",
      he: "קוראים מאות, עשרות ואחדות במספר תלת-ספרתי, גם כשיש אפס במקום העשרות.",
    },
  },
  {
    slug: "multiplication",
    gradeMin: 3,
    gradeMax: 4,
    breadcrumbGrade: 3,
    skillId: "arithmetic.multiply-core",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Multiplication", he: "כפל" },
    summary: {
      en: "Build multiplication facts from equal groups, including the harder one-digit facts.",
      he: "בונים עובדות כפל מקבוצות שוות, כולל עובדות הכפל הקשות יותר של ספרה אחת.",
    },
  },
  {
    slug: "division-facts",
    gradeMin: 3,
    gradeMax: 3,
    breadcrumbGrade: 3,
    skillId: "arithmetic.divide-exact",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Division facts", he: "עובדות חילוק" },
    summary: {
      en: "Divide when the quotient is a whole number and check by multiplying back.",
      he: "מחלקים כשהמנה היא מספר שלם, ובודקים על ידי כפל חוזר.",
    },
  },
  {
    slug: "decimals",
    gradeMin: 4,
    gradeMax: 6,
    breadcrumbGrade: 5,
    skillId: "arithmetic.decimals-add",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Decimals", he: "מספרים עשרוניים" },
    summary: {
      en: "Compare decimals by place value, then add them by lining up the decimal point.",
      he: "משווים מספרים עשרוניים לפי ערך מקום, ואז מחברים אותם כשמיישרים את הנקודה.",
    },
  },
  {
    slug: "area-and-perimeter",
    gradeMin: 4,
    gradeMax: 5,
    breadcrumbGrade: 4,
    skillId: "geometry.rectangle-area",
    level: "elementary",
    subject: "Geometry",
    navLabel: { en: "Area and perimeter", he: "שטח והיקף" },
    summary: {
      en: "Find the area and the perimeter of a rectangle from its length and width.",
      he: "מוצאים שטח והיקף של מלבן לפי האורך והרוחב.",
    },
  },
  {
    slug: "angles",
    gradeMin: 5,
    gradeMax: 6,
    breadcrumbGrade: 5,
    skillId: "geometry.angle-sum",
    level: "elementary",
    subject: "Geometry",
    navLabel: { en: "Angles", he: "זוויות" },
    summary: {
      en: "Find the missing angle in a triangle when the other two angles are known.",
      he: "מוצאים זווית חסרה במשולש כששתי הזוויות האחרות ידועות.",
    },
  },
  {
    slug: "powers-of-two",
    gradeMin: 5,
    gradeMax: 6,
    breadcrumbGrade: 5,
    skillId: "arithmetic.power-of-two",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Powers of two", he: "חזקות של 2" },
    summary: {
      en: "Evaluate a power of two by multiplying that many factors of 2.",
      he: "מחשבים חזקה של 2 על ידי כפל של אותו מספר גורמים של 2.",
    },
  },
  {
    slug: "percentages",
    gradeMin: 5,
    gradeMax: 8,
    breadcrumbGrade: 6,
    skillId: "arithmetic.percent-of",
    level: "elementary",
    subject: "General",
    navLabel: { en: "Percentages", he: "אחוזים" },
    summary: {
      en: "Find a percent of a number, then describe a price change as a percent increase.",
      he: "מוצאים אחוז ממספר, ואחר כך מתארים שינוי מחיר כאחוז עלייה.",
    },
  },
  {
    slug: "ratios",
    gradeMin: 7,
    gradeMax: 8,
    breadcrumbGrade: 7,
    skillId: "arithmetic.ratio-parts",
    level: "middle",
    subject: "General",
    navLabel: { en: "Ratios", he: "יחסים" },
    summary: {
      en: "Share an amount in a given ratio by splitting it into equal parts first.",
      he: "מחלקים כמות ביחס נתון, אחרי שמפצלים אותה לחלקים שווים.",
    },
  },
  {
    slug: "box-volume",
    gradeMin: 7,
    gradeMax: 8,
    breadcrumbGrade: 7,
    skillId: "geometry.box-volume",
    level: "middle",
    subject: "Geometry",
    navLabel: { en: "Volume of a box", he: "נפח של תיבה" },
    summary: {
      en: "Multiply length, width, and height to find how much space a box holds.",
      he: "כופלים אורך, רוחב וגובה כדי למצוא כמה מקום תיבה תופסת.",
    },
  },
  {
    slug: "pythagorean-theorem",
    gradeMin: 8,
    gradeMax: 8,
    breadcrumbGrade: 8,
    skillId: "geometry.pythagorean",
    level: "middle",
    subject: "Geometry",
    navLabel: { en: "Pythagorean theorem", he: "משפט פיתגורס" },
    summary: {
      en: "Find the hypotenuse of a right triangle from the two shorter sides.",
      he: "מוצאים את היתר של משולש ישר-זווית לפי שתי הצלעות הקצרות.",
    },
  },
  {
    slug: "systems-of-equations",
    gradeMin: 8,
    gradeMax: 8,
    breadcrumbGrade: 8,
    skillId: "algebra.system",
    level: "middle",
    subject: "Algebra",
    navLabel: { en: "Systems of equations", he: "מערכות משוואות" },
    summary: {
      en: "Solve two linear equations together so one pair of values fits both.",
      he: "פותרים שתי משוואות לינאריות יחד, כך שזוג ערכים אחד מתאים לשתיהן.",
    },
  },
  {
    slug: "inequalities",
    gradeMin: 8,
    gradeMax: 8,
    breadcrumbGrade: 8,
    skillId: "algebra.linear-inequality",
    level: "middle",
    subject: "Algebra",
    navLabel: { en: "Inequalities", he: "אי-שוויונות" },
    summary: {
      en: "Solve a linear inequality and keep the solution as a range, not a single number.",
      he: "פותרים אי-שוויון לינארי ומשאירים את הפתרון כתחום, לא כמספר יחיד.",
    },
  },
  {
    slug: "exponents",
    gradeMin: 7,
    gradeMax: 8,
    breadcrumbGrade: 7,
    skillId: "algebra.expand-binomial",
    level: "middle",
    subject: "Algebra",
    navLabel: { en: "Exponents", he: "חזקות" },
    summary: {
      en: "Read a power as repeated multiplication, then expand a product such as (x+2)(x+3).",
      he: "קוראים חזקה ככפל חוזר, ואז פותחים מכפלה כמו (x+2)(x+3).",
    },
  },
];

const GRADE_3_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "arithmetic.equivalent-fractions",
    level: "elementary",
    subject: "General",
    name: { en: "Equivalent fractions", he: "שברים שקולים" },
    sentence: {
      en: "Rename one half as a fraction with more equal parts.",
      he: "כותבים את החצי כשבר עם יותר חלקים שווים.",
    },
  },
  {
    skillId: "arithmetic.compare-fractions-same-den",
    level: "elementary",
    subject: "General",
    name: { en: "Compare fractions", he: "השוואת שברים" },
    sentence: {
      en: "Decide which fraction is larger when the pieces are the same size.",
      he: "קובעים איזה שבר גדול יותר כשהחלקים באותו גודל.",
    },
  },
  {
    skillId: "arithmetic.multiply-easy",
    level: "elementary",
    subject: "General",
    name: { en: "Multiplication by 2, 5, and 10", he: "כפל ב-2, ב-5 וב-10" },
    sentence: {
      en: "Use equal groups to multiply by 2, 5, or 10.",
      he: "משתמשים בקבוצות שוות כדי לכפול ב-2, ב-5 או ב-10.",
    },
  },
];

const GRADE_4_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "arithmetic.add-fractions-same-den",
    level: "elementary",
    subject: "General",
    name: { en: "Add fractions", he: "חיבור שברים" },
    sentence: {
      en: "Add fractions that already share a denominator.",
      he: "מחברים שברים שהמכנה שלהם כבר שווה.",
    },
  },
  {
    skillId: "arithmetic.divide-remainder",
    level: "elementary",
    subject: "General",
    name: { en: "Division with a remainder", he: "חילוק עם שארית" },
    sentence: {
      en: "Divide and leave a remainder smaller than the divisor.",
      he: "מחלקים ומשאירים שארית קטנה מהמחלק.",
    },
  },
  {
    skillId: "geometry.rectangle-area",
    level: "elementary",
    subject: "Geometry",
    name: { en: "Rectangle area", he: "שטח מלבן" },
    sentence: {
      en: "Find the area of a rectangle from its length and width.",
      he: "מוצאים שטח של מלבן לפי האורך והרוחב.",
    },
  },
];

const GRADE_5_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "arithmetic.pemdas",
    level: "elementary",
    subject: "General",
    name: { en: "Order of operations", he: "סדר פעולות" },
    sentence: {
      en: "Simplify an expression by doing parentheses, then multiplication and division.",
      he: "מפשטים ביטוי: קודם סוגריים, ואחר כך כפל וחילוק.",
    },
  },
  {
    skillId: "arithmetic.add-fractions-related",
    level: "elementary",
    subject: "General",
    name: { en: "Add related fractions", he: "חיבור שברים עם מכנים קשורים" },
    sentence: {
      en: "Add fractions whose denominators are related, such as fourths and eighths.",
      he: "מחברים שברים שהמכנים שלהם קשורים, למשל רבעים ושמיניות.",
    },
  },
  {
    skillId: "stats.mean",
    level: "elementary",
    subject: "Statistics",
    name: { en: "Mean", he: "ממוצע" },
    sentence: {
      en: "Find the mean of a short list of numbers.",
      he: "מוצאים את הממוצע של רשימת מספרים קצרה.",
    },
  },
];

const GRADE_6_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "arithmetic.pemdas",
    level: "elementary",
    subject: "General",
    name: { en: "Order of operations", he: "סדר פעולות" },
    sentence: {
      en: "Use the order of operations on an expression with more than one step.",
      he: "משתמשים בסדר הפעולות בביטוי שיש בו יותר מצעד אחד.",
    },
  },
  {
    skillId: "arithmetic.percent-simple-core",
    level: "elementary",
    subject: "General",
    name: { en: "Ten percent and twenty-five percent", he: "עשרה אחוזים ורבע" },
    sentence: {
      en: "Find 10% or 25% of a number.",
      he: "מוצאים 10% או 25% ממספר.",
    },
  },
  {
    skillId: "arithmetic.unit-price",
    level: "elementary",
    subject: "General",
    name: { en: "Unit price", he: "מחיר ליחידה" },
    sentence: {
      en: "Find the price of one item from the price of several.",
      he: "מוצאים את המחיר של פריט אחד מתוך המחיר של כמה פריטים.",
    },
  },
];

const GRADE_7_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "algebra.linear-one-step",
    level: "middle",
    subject: "Algebra",
    name: { en: "Solve 2x + 3 = 11", he: "פתרון 2x + 3 = 11" },
    sentence: {
      en: "Undo addition and then division to solve for x.",
      he: "מבטלים חיבור ואחר כך חילוק כדי למצוא את x.",
    },
  },
  {
    skillId: "arithmetic.ratio-parts",
    level: "middle",
    subject: "General",
    name: { en: "Share in a ratio", he: "חלוקה ביחס" },
    sentence: {
      en: "Split an amount into parts that follow a given ratio.",
      he: "מחלקים כמות לחלקים לפי יחס נתון.",
    },
  },
  {
    skillId: "geometry.box-volume",
    level: "middle",
    subject: "Geometry",
    name: { en: "Volume of a box", he: "נפח תיבה" },
    sentence: {
      en: "Find the volume of a box from length, width, and height.",
      he: "מוצאים נפח של תיבה לפי אורך, רוחב וגובה.",
    },
  },
];

const GRADE_8_SAMPLES: readonly GradeSample[] = [
  {
    skillId: "algebra.slope",
    level: "middle",
    subject: "Algebra",
    name: { en: "Slope between two points", he: "שיפוע בין שתי נקודות" },
    sentence: {
      en: "Find how steep a line is from two points on it.",
      he: "מוצאים עד כמה ישר תלול לפי שתי נקודות שעליו.",
    },
  },
  {
    skillId: "algebra.system",
    level: "middle",
    subject: "Algebra",
    name: { en: "System of equations", he: "מערכת משוואות" },
    sentence: {
      en: "Solve a pair of linear equations for both unknowns.",
      he: "פותרים זוג משוואות לינאריות לשני הנעלמים.",
    },
  },
  {
    skillId: "geometry.pythagorean",
    level: "middle",
    subject: "Geometry",
    name: { en: "Pythagorean theorem", he: "משפט פיתגורס" },
    sentence: {
      en: "Find a side of a right triangle from the other two sides.",
      he: "מוצאים צלע במשולש ישר-זווית לפי שתי הצלעות האחרות.",
    },
  },
];

export const GRADE_HUBS: readonly GradeHub[] = [
  {
    grade: 3,
    samples: GRADE_3_SAMPLES,
    en: {
      title: "3rd grade math practice — Math Whiteboard",
      description:
        "Third grade math practice: hundreds place value, multiplication facts, exact division, and fractions with equal parts. Open a lesson or start a grade 3 exercise.",
      keywords:
        "3rd grade math practice, grade 3 place value, multiplication facts, division facts, fractions",
      heading: "Grade 3 math practice",
      paragraphs: [
        "Grade 3 reads the hundreds digit in a three-digit number, builds multiplication facts from equal groups, and divides when the answer is a whole number. Fractions on this grade are pictures of equal parts, including equivalent fractions.",
        "Place value and division facts are grade 3 lessons. Multiplication is shared with grade 4, and the fractions lesson continues through grade 5. Each lesson has one address, and this hub links to it.",
      ],
      cta: "Practice grade 3 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ג — לוח מתמטיקה",
      description:
        "תרגול לכיתה ג: ערך המאות, עובדות כפל, חילוק מדויק, ושברים עם חלקים שווים. פותחים שיעור או מתחילים תרגיל לכיתה ג.",
      keywords: "תרגול כיתה ג, ערך מקום כיתה ג, עובדות כפל, עובדות חילוק, שברים",
      heading: "תרגול מתמטיקה לכיתה ג",
      paragraphs: [
        "בכיתה ג קוראים את ספרת המאות במספר תלת-ספרתי, בונים עובדות כפל מקבוצות שוות, ומחלקים כשהתשובה היא מספר שלם. השברים בכיתה הזו הם ציורים של חלקים שווים, כולל שברים שקולים.",
        "ערך מקום ועובדות חילוק הם שיעורים של כיתה ג. הכפל משותף גם לכיתה ד, ושיעור השברים נמשך עד כיתה ה. לכל שיעור יש כתובת אחת, והעמוד הזה מקשר אליה.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ג",
    },
  },
  {
    grade: 4,
    samples: GRADE_4_SAMPLES,
    en: {
      title: "4th grade math practice — Math Whiteboard",
      description:
        "Fourth grade math practice: multiplication facts, adding fractions, rectangle area and perimeter, long division, and comparing decimals.",
      keywords:
        "4th grade math practice, grade 4 fractions, long division grade 4, rectangle area, decimals",
      heading: "Grade 4 math practice",
      paragraphs: [
        "Grade 4 is where multiplication facts get harder, long division keeps a remainder, and fraction pictures turn into addition. Students also find the area and the perimeter of a rectangle and start comparing decimals.",
        "The fractions lesson is the same page grades 3 and 5 use. Area and perimeter continue into grade 5, and decimals continue through grade 6. This hub links those lessons instead of copying them.",
      ],
      cta: "Practice grade 4 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ד — לוח מתמטיקה",
      description:
        "תרגול לכיתה ד: עובדות כפל, חיבור שברים, שטח והיקף של מלבן, חילוק ארוך, והשוואת מספרים עשרוניים.",
      keywords: "תרגול כיתה ד, שברים כיתה ד, חילוק ארוך כיתה ד, שטח מלבן, מספרים עשרוניים",
      heading: "תרגול מתמטיקה לכיתה ד",
      paragraphs: [
        "בכיתה ד עובדות הכפל נעשות קשות יותר, בחילוק ארוך נשארת שארית, וציורי השברים הופכים לחיבור. מוצאים גם שטח והיקף של מלבן, ומתחילים להשוות מספרים עשרוניים.",
        "שיעור השברים הוא אותו עמוד של כיתות ג ו-ה. שטח והיקף נמשכים לכיתה ה, והמספרים העשרוניים נמשכים עד כיתה ו. העמוד הזה מקשר אל השיעורים ולא מעתיק אותם.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ד",
    },
  },
  {
    grade: 5,
    samples: GRADE_5_SAMPLES,
    en: {
      title: "5th grade math practice — Math Whiteboard",
      description:
        "Fifth grade math practice: decimals, rectangle area, order of operations, triangle angles, powers of two, and percent of a number.",
      keywords:
        "5th grade math practice, grade 5 decimals, order of operations, triangle angles, percent",
      heading: "Grade 5 math practice",
      paragraphs: [
        "Grade 5 adds decimals, follows the order of operations, and finds a missing angle in a triangle. Powers of two are the exponent exercises in the catalog, and percent of a number sits beside the fractions lesson from earlier grades.",
        "Fractions and area are shared with grade 4. Order of operations, angles, powers of two, and percents continue into later grades. This page links each lesson once.",
      ],
      cta: "Practice grade 5 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ה — לוח מתמטיקה",
      description:
        "תרגול לכיתה ה: מספרים עשרוניים, שטח מלבן, סדר פעולות, זוויות במשולש, חזקות של 2, ואחוז ממספר.",
      keywords: "תרגול כיתה ה, מספרים עשרוניים כיתה ה, סדר פעולות, זוויות במשולש, אחוזים",
      heading: "תרגול מתמטיקה לכיתה ה",
      paragraphs: [
        "בכיתה ה מחברים מספרים עשרוניים, עובדים לפי סדר הפעולות, ומוצאים זווית חסרה במשולש. חזקות של 2 הן תרגילי החזקות שבקטלוג, ואחוז ממספר יושב ליד שיעור השברים מהכיתות הקודמות.",
        "שברים ושטח משותפים גם לכיתה ד. סדר פעולות, זוויות, חזקות של 2 ואחוזים נמשכים לכיתות הבאות. העמוד הזה מקשר כל שיעור פעם אחת.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ה",
    },
  },
  {
    grade: 6,
    samples: GRADE_6_SAMPLES,
    en: {
      title: "6th grade math practice — Math Whiteboard",
      description:
        "Sixth grade math practice: adding decimals, order of operations, triangle angles, powers of two, and percent of a number.",
      keywords:
        "6th grade math practice, grade 6 decimals, order of operations, percent of a number, angles",
      heading: "Grade 6 math practice",
      paragraphs: [
        "Grade 6 adds decimals, uses the order of operations on longer expressions, and finds 10% and 25% of a number. Triangle angles and powers of two are the same lessons grade 5 uses.",
        "None of those lessons is copied onto this page. Each one has a single address. Unit price stays a sample exercise beside the lessons.",
      ],
      cta: "Practice grade 6 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ו — לוח מתמטיקה",
      description:
        "תרגול לכיתה ו: חיבור מספרים עשרוניים, סדר פעולות, זוויות במשולש, חזקות של 2, ואחוז ממספר.",
      keywords: "תרגול כיתה ו, מספרים עשרוניים כיתה ו, סדר פעולות, אחוז ממספר, זוויות",
      heading: "תרגול מתמטיקה לכיתה ו",
      paragraphs: [
        "בכיתה ו מחברים מספרים עשרוניים, משתמשים בסדר הפעולות בביטויים ארוכים יותר, ומוצאים 10% ו-25% ממספר. זוויות במשולש וחזקות של 2 הם אותם שיעורים של כיתה ה.",
        "אף אחד מהשיעורים האלה לא מועתק לעמוד הזה. לכל אחד יש כתובת אחת. מחיר ליחידה נשאר תרגיל דוגמה ליד השיעורים.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ו",
    },
  },
  {
    grade: 7,
    samples: GRADE_7_SAMPLES,
    en: {
      title: "7th grade math practice — Math Whiteboard",
      description:
        "Seventh grade math practice: percent of a number, linear equations such as 2x + 3 = 11, sharing in a ratio, and the volume of a box.",
      keywords:
        "7th grade math practice, grade 7 linear equations, ratio, volume of a box, percent",
      heading: "Grade 7 math practice",
      paragraphs: [
        "Grade 7 treats an equation as a balance, shares an amount in a ratio, and multiplies three edges to find the volume of a box. Percent of a number continues from grades 5 and 6 into percent increase.",
        "The percent, linear equations, ratio, and volume lessons are shared with grade 8 and linked from here. Each lesson has one URL. Solving 2x + 3 = 11 is the practice skill on the equations page.",
      ],
      cta: "Practice grade 7 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ז — לוח מתמטיקה",
      description:
        "תרגול לכיתה ז: אחוז ממספר, משוואות לינאריות כמו 2x + 3 = 11, חלוקה ביחס, ונפח של תיבה.",
      keywords: "תרגול כיתה ז, משוואות לינאריות כיתה ז, יחס, נפח תיבה, אחוזים",
      heading: "תרגול מתמטיקה לכיתה ז",
      paragraphs: [
        "בכיתה ז מתייחסים למשוואה כאל מאזניים, מחלקים כמות ביחס, וכופלים שלוש צלעות כדי למצוא נפח של תיבה. אחוז ממספר נמשך מכיתות ה ו-ו אל אחוז עלייה.",
        "שיעורי האחוזים, המשוואות, היחס והנפח משותפים גם לכיתה ח ומקושרים מכאן. לכל שיעור יש כתובת אחת. פתרון 2x + 3 = 11 הוא מיומנות התרגול בעמוד המשוואות.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ז",
    },
  },
  {
    grade: 8,
    samples: GRADE_8_SAMPLES,
    en: {
      title: "8th grade math practice — Math Whiteboard",
      description:
        "Eighth grade math practice: linear equations, ratios, volume, the Pythagorean theorem, systems of equations, and inequalities.",
      keywords:
        "8th grade math practice, grade 8 Pythagorean theorem, system of equations, inequalities, slope",
      heading: "Grade 8 math practice",
      paragraphs: [
        "Grade 8 keeps the grades 7–8 lessons and adds the right triangle, a pair of equations, and a linear inequality. Slope between two points stays a sample exercise next to those lessons.",
        "The Pythagorean theorem, systems, and inequalities are grade 8 lessons with their own addresses. The linear equations page is still the one grades 7 and 8 share. The catalog also offers these geometry and algebra skills in grade 9, and this hub stops at grade 8.",
      ],
      cta: "Practice grade 8 math",
    },
    he: {
      title: "תרגול מתמטיקה לכיתה ח — לוח מתמטיקה",
      description:
        "תרגול לכיתה ח: משוואות לינאריות, יחסים, נפח, משפט פיתגורס, מערכות משוואות, ואי-שוויונות.",
      keywords: "תרגול כיתה ח, משפט פיתגורס כיתה ח, מערכת משוואות, אי-שוויון, שיפוע",
      heading: "תרגול מתמטיקה לכיתה ח",
      paragraphs: [
        "בכיתה ח נשארים שיעורי כיתות ז–ח, ונוספים המשולש ישר-הזווית, זוג משוואות, ואי-שוויון לינארי. שיפוע בין שתי נקודות נשאר תרגיל דוגמה ליד השיעורים האלה.",
        "משפט פיתגורס, מערכות ואי-שוויונות הם שיעורים של כיתה ח עם כתובת משלהם. עמוד המשוואות הלינאריות הוא עדיין העמוד המשותף לכיתות ז ו-ח. הקטלוג מציע את מיומנויות הגיאומטריה והאלגברה האלה גם בכיתה ט, והעמוד הזה נעצר בכיתה ח.",
      ],
      cta: "לתרגל מתמטיקה לכיתה ח",
    },
  },
];

export function gradeTopicBySlug(slug: string): GradeTopicLink | null {
  return GRADE_TOPIC_LINKS.find((link) => link.slug === slug) ?? null;
}

export function topicsForGrade(grade: number): GradeTopicLink[] {
  return GRADE_TOPIC_LINKS.filter(
    (link) => grade >= link.gradeMin && grade <= link.gradeMax,
  );
}

export function gradesForTopic(slug: string): number[] {
  const link = gradeTopicBySlug(slug);
  if (!link) return [];
  const grades: number[] = [];
  for (let grade = link.gradeMin; grade <= link.gradeMax; grade += 1) {
    grades.push(grade);
  }
  return grades;
}

export function gradeHubByGrade(grade: number): GradeHub | null {
  return GRADE_HUBS.find((hub) => hub.grade === grade) ?? null;
}

export function gradeHubPath(grade: number, lang: PracticeLocale): string {
  return lang === "he" ? `/he/grade-${grade}` : `/grade-${grade}`;
}

export function gradeHubLink(
  grade: number,
  lang: PracticeLocale,
): { href: string; text: string } {
  return {
    href: gradeHubPath(grade, lang),
    text: gradeFullLabel(grade, lang),
  };
}

export function gradeHubFromPath(
  pathname: string,
): { grade: number; lang: PracticeLocale } | null {
  const match = pathname.match(/^\/(?:(he)\/)?grade-(\d+)$/);
  if (!match) return null;
  const grade = Number(match[2]);
  if (!gradeHubByGrade(grade)) return null;
  return { grade, lang: match[1] === "he" ? "he" : "en" };
}

export function isGradeHubPath(pathname: string): boolean {
  return gradeHubFromPath(pathname) != null;
}

export function practiceSearchHref(
  lang: PracticeLocale,
  query: {
    grade?: number;
    level?: PracticeBand;
    subject?: string;
    skillId?: string;
  },
): string {
  const params = new URLSearchParams();
  if (query.grade != null) params.set("grade", String(query.grade));
  if (query.level) params.set("level", query.level);
  if (query.subject) params.set("subject", query.subject);
  if (query.skillId) params.set("skill", query.skillId);
  const base = lang === "he" ? "/he/practice" : "/practice";
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function topicPathFor(slug: string, lang: PracticeLocale): string {
  return lang === "he" ? `/he/math/${slug}` : `/math/${slug}`;
}

export type GradeHubView = {
  path: string;
  title: string;
  description: string;
  keywords: string;
  heading: string;
  paragraphs: string[];
  topicsHeading: string;
  topics: { href: string; label: string; summary: string }[];
  samplesHeading: string;
  samples: { href: string; name: string; sentence: string }[];
  ctaHref: string;
  ctaLabel: string;
  otherGrades: { href: string; label: string }[];
};

export function gradeHubView(
  grade: number,
  lang: PracticeLocale,
): GradeHubView | null {
  const hub = gradeHubByGrade(grade);
  if (!hub) return null;
  const copy = lang === "he" ? hub.he : hub.en;
  const level = bandForGrade(grade);
  return {
    path: gradeHubPath(grade, lang),
    title: copy.title,
    description: copy.description,
    keywords: copy.keywords,
    heading: copy.heading,
    paragraphs: copy.paragraphs,
    topicsHeading: lang === "he" ? "נושאים" : "Topics",
    topics: topicsForGrade(grade).map((link) => ({
      href: topicPathFor(link.slug, lang),
      label: link.navLabel[lang],
      summary: link.summary[lang],
    })),
    samplesHeading: lang === "he" ? "תרגילים לדוגמה" : "Sample exercises",
    samples: hub.samples.map((sample) => ({
      href: practiceSearchHref(lang, {
        grade,
        level: sample.level,
        subject: sample.subject,
        skillId: sample.skillId,
      }),
      name: sample.name[lang],
      sentence: sample.sentence[lang],
    })),
    ctaHref: practiceSearchHref(lang, { grade, level }),
    ctaLabel: copy.cta,
    otherGrades: GRADE_HUBS.filter((other) => other.grade !== grade).map(
      (other) => ({
        href: gradeHubPath(other.grade, lang),
        label: gradeFullLabel(other.grade, lang),
      }),
    ),
  };
}

export function topicGradeLabel(slug: string, lang: PracticeLocale): string {
  const link = gradeTopicBySlug(slug);
  if (!link) return "";
  return gradeSpanLabel(link.gradeMin, link.gradeMax, lang);
}

export type Crumb = { name: string; url: string };

export function topicBreadcrumbs(
  slug: string,
  lang: PracticeLocale,
  origin = "https://themathboard.com",
): Crumb[] {
  const link = gradeTopicBySlug(slug);
  if (!link) return [];
  const home = origin.endsWith("/") ? origin : `${origin}/`;
  return [
    { name: lang === "he" ? "דף הבית" : "Home", url: home },
    {
      name: gradeFullLabel(link.breadcrumbGrade, lang),
      url: `${origin}${gradeHubPath(link.breadcrumbGrade, lang)}`,
    },
    {
      name: link.navLabel[lang],
      url: `${origin}${topicPathFor(slug, lang)}`,
    },
  ];
}
